import type { DetectOptions } from './types';
import path from 'node:path';

export const AMBIGUOUS_ENV_TERMINALS = new Set([
  'linux_console',
  'truecolor_terminal',
  'vt100',
  'xterm',
  'xterm_256color',
  'xterm_truecolor'
]);

export const SHELL_PROCESS_NAMES = new Set([
  'bash',
  'csh',
  'dash',
  'fish',
  'ksh',
  'login',
  'node',
  'nodejs',
  'nu',
  'sh',
  'sudo',
  'tcsh',
  'zsh'
]);

export const normalizeIdentifier = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');
};

export const normalizeAppName = (value: string, platform: NodeJS.Platform): string => {
  const appName = platform === 'darwin' ? path.parse(value).name : value;
  return normalizeIdentifier(appName);
};

export const isAmbiguousEnvResult = (
  terminal: string | null,
  options: DetectOptions = {}
): boolean => {
  if (!terminal) {
    return true;
  }

  if (options.preferOuter && (terminal === 'tmux' || terminal === 'screen')) {
    return true;
  }

  return AMBIGUOUS_ENV_TERMINALS.has(terminal);
};

export const hasKonsoleEnvironment = (env: NodeJS.ProcessEnv): boolean => {
  return Object.keys(env).some(envVar => /konsole/i.test(envVar));
};

export const normalizeParentProcessName = (processName: string): string | null => {
  const normalized = normalizeIdentifier(path.parse(processName).name);

  switch (normalized) {
    case 'alacritty':
    case 'aterm':
    case 'atomic_terminal':
    case 'eterm':
    case 'foot':
    case 'ghostty':
    case 'hyper':
    case 'kitty':
    case 'konsole':
    case 'kuake':
    case 'mate_terminal':
    case 'mrxvt':
    case 'powershell':
    case 'putty':
    case 'qterminal':
    case 'rio':
    case 'rxvt':
    case 'terminator':
    case 'terminology':
    case 'tilda':
    case 'tmux':
    case 'warp':
    case 'wezterm':
    case 'wterm':
      return normalized;
    case 'apple_terminal':
    case 'terminal':
      return 'terminal';
    case 'cmd':
      return 'cmd';
    case 'code':
    case 'code_insiders':
      return 'vscode';
    case 'conhost':
      return 'conhost';
    case 'gnome':
    case 'gnome_terminal':
    case 'gnome_terminal_server':
    case 'guake':
      return 'gnome_terminal';
    case 'iterm':
    case 'iterm2':
      return 'iterm';
    case 'login':
    case 'linux':
      return 'linux_console';
    case 'pwsh':
      return 'powershell';
    case 'screen':
      return 'screen';
    case 'terminal_app':
      return 'terminal_app';
    case 'urxvt':
    case 'urxvt256c':
    case 'urxvt256c_ml':
    case 'urxvt_ml':
      return 'rxvt';
    case 'wt':
    case 'windows_terminal':
      return 'windows_terminal';
    case 'xfce':
    case 'xfce_terminal':
    case 'xfce4_terminal':
      return 'xfce4_terminal';
    default: {
      if (normalized.startsWith('gnome_terminal')) {
        return 'gnome_terminal';
      }

      if (normalized.startsWith('warp')) {
        return 'warp';
      }

      return null;
    }
  }
};
